/**
 * What media-api's delivery and performance endpoints must send for this app to render them.
 *
 * Every object is `looseObject`, never `strictObject`: the API contract is additive within a
 * major version, so a field we do not know about has to survive the parse rather than be
 * stripped or rejected. What each schema does assert is the structure the mappers and the
 * screens index into - an array is an array, a number is a number, a nullable field is
 * present.
 *
 * The `z.ZodType<T>` annotations are load-bearing. They make `tsc` fail when a schema stops
 * producing the type its consumers read, so a field added to one of those types cannot be
 * left unvalidated here.
 */
import { z } from 'zod';
import { wireUnion } from '@/lib/api/parse-response';
import type { ProjectPerformance } from '../model/performance.types';
import type { ListPlatformFieldSchemasResponse } from '../model/platform-schema.types';
import type { ConnectorCapabilities } from '../model/types';
import type {
  ApiJob,
  ApiJobIssue,
  ApiJobList,
  ApiJobNode,
  ApiJobPlan,
  ApiJobPublishReport,
} from './job-mappers';
import type { JobHistoryEntry } from './job-queries';
import type { PreflightResult } from './job-mutations';
import type {
  DiscoveredCampaign,
  PlatformCampaignOption,
  ProjectCampaign,
} from './campaign-list-queries';

const jsonObject = z.looseObject({});

const capabilitySchema = z.looseObject({
  state: wireUnion<ConnectorCapabilities['signIn']['state']>(),
  checkedAt: z.string().nullable(),
  error: z.string().nullable(),
});

const capabilitiesSchema: z.ZodType<ConnectorCapabilities> = z.looseObject({
  signIn: capabilitySchema,
  publishing: capabilitySchema,
  reporting: capabilitySchema,
});

const jobPlatformSchema = z.looseObject({
  platform: z.string(),
  connectorId: z.string().nullable(),
});

const jobRunSummarySchema = z.looseObject({
  lastRunAt: z.string(),
  errors: z.number(),
  warnings: z.number(),
  advice: z.number(),
  isStale: z.boolean(),
});

const dryRunSummarySchema = z.looseObject({
  lastRunAt: z.string(),
  checked: z.number(),
  rejected: z.number(),
  unchecked: z.number(),
});

export const jobSchema: z.ZodType<ApiJob> = z.looseObject({
  id: z.string(),
  projectId: z.string().nullable(),
  title: z.string(),
  status: z.string(),
  sourceFileName: z.string(),
  sourceFileKind: z.string(),
  sourceRowCount: z.number(),
  sourceTemplateName: z.string().nullable(),
  sourceError: z.string().nullable().optional(),
  isReadingSource: z.boolean().optional(),
  adCount: z.number().nullable().optional(),
  hasSource: z.boolean().optional(),
  updatedAt: z.string().optional(),
  platforms: z.array(jobPlatformSchema),
  createdBy: z.string().nullable(),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  preflight: jobRunSummarySchema.nullable(),
  dryRun: dryRunSummarySchema.nullable(),
});

export const jobListSchema: z.ZodType<ApiJobList> = z.looseObject({
  items: z.array(jobSchema),
  total: z.number(),
});

const jobIssueSchema: z.ZodType<ApiJobIssue> = z.looseObject({
  fieldKey: z.string(),
  severity: z.string(),
  message: z.string(),
  sourceRow: z.number().optional(),
  source: z.string().optional(),
  ruleId: z.string().optional(),
  guidelineId: z.string().optional(),
  why: z.string().optional(),
  howToFix: z.string().optional(),
  referenceUrl: z.string().optional(),
  ownerName: z.string().optional(),
});

/**
 * Recursive, and annotated because it has to be: the plan tree nests a creative under an ad
 * under an ad set, and `toJobNode` walks it to the bottom.
 */
const jobNodeSchema: z.ZodType<ApiJobNode> = z.lazy(() =>
  z.looseObject({
    id: z.string(),
    level: z.string(),
    name: z.string(),
    action: z.string(),
    externalId: z.string().nullable(),
    sourceRows: z.array(z.number()),
    fields: jsonObject,
    editedFields: z.array(z.string()),
    issues: z.array(jobIssueSchema),
    publishOutcome: z.string().optional(),
    publishError: z.string().nullable().optional(),
    deliveryState: z.string().nullable().optional(),
    children: z.array(jobNodeSchema),
  }),
);

export const jobPlanSchema: z.ZodType<ApiJobPlan> = z.looseObject({
  jobId: z.string(),
  platforms: z.array(
    z.looseObject({
      platform: z.string(),
      connectorId: z.string().nullable(),
      pageId: z.string().nullable().optional(),
      pageName: z.string().nullable().optional(),
      advertiserId: z.string().nullable().optional(),
      advertiserName: z.string().nullable().optional(),
      needsAdvertiser: z.boolean().optional(),
      connectorStatus: z.string().nullable().optional(),
      connectorCapabilities: capabilitiesSchema.nullable().optional(),
      accountName: z.string().nullable().optional(),
      dryRunSupported: z.boolean().optional(),
      nodes: z.array(jobNodeSchema),
    }),
  ),
});

export const jobPublishReportSchema: z.ZodType<ApiJobPublishReport> = z.looseObject({
  jobId: z.string(),
  status: z.string(),
  created: z.number(),
  updated: z.number(),
  unchanged: z.number(),
  skipped: z.number(),
  failed: z.number(),
  live: z.number().optional(),
  inReview: z.number().optional(),
  rejected: z.number().optional(),
  lastDeliveryCheckAt: z.string().nullable().optional(),
  results: z.array(
    z.looseObject({
      nodeId: z.string(),
      platform: z.string(),
      level: z.string(),
      name: z.string(),
      outcome: z.string(),
      externalId: z.string().nullable(),
      reason: z.string().nullable(),
      deliveryState: z.string().nullable().optional(),
      deliveryDetail: z.string().nullable().optional(),
    }),
  ),
});

/** The pre-flight verdict, which arrives with the re-checked plan attached. */
export const preflightSchema: z.ZodType<PreflightResult & { plan: ApiJobPlan }> = z.looseObject({
  errors: z.number(),
  warnings: z.number(),
  unboundPlatforms: z.array(z.string()),
  unusableAccounts: z.array(
    z.looseObject({
      platform: z.string(),
      name: z.string(),
      status: z.string().optional(),
      reason: wireUnion<PreflightResult['unusableAccounts'][number]['reason']>(),
      detail: z.string().optional(),
    }),
  ),
  uncheckedPlatforms: z.array(z.string()),
  canPublish: z.boolean(),
  plan: jobPlanSchema,
});

export const jobHistorySchema: z.ZodType<JobHistoryEntry[]> = z.array(
  z.looseObject({
    id: z.string(),
    actorId: z.string(),
    action: z.string(),
    reason: z.string().nullable(),
    nodeId: z.string().nullable(),
    metadata: jsonObject,
    createdAt: z.string(),
  }),
);

const namedOptionSchema = z.looseObject({ id: z.string(), name: z.string() });

/**
 * `items` and `supported` are optional on the wire and defaulted by the caller, so the
 * schema keeps them optional too - but an `items` that is present and not an array is the
 * failure this guards, since the caller's `?? []` cannot see it.
 */
export const connectorAdvertisersSchema = z.looseObject({
  items: z.array(namedOptionSchema).optional(),
  supported: z.boolean().optional(),
});

export const connectorPagesSchema = z.looseObject({
  items: z.array(namedOptionSchema).optional(),
  supported: z.boolean().optional(),
});

export const connectorAccountsSchema = z.looseObject({
  items: z.array(
    z.looseObject({
      id: z.string(),
      platform: z.string(),
      title: z.string(),
      status: z.string(),
      externalAccountId: z.string().nullable(),
      externalAccountName: z.string().nullable(),
    }),
  ),
});

export const projectCampaignsSchema: z.ZodType<{ items: ProjectCampaign[] }> = z.looseObject({
  items: z.array(
    z.looseObject({
      id: z.string(),
      name: z.string(),
      platform: z.string().nullable(),
      connectorId: z.string().nullable(),
      synced: z.boolean(),
      channel: z.string().nullable(),
      status: z.string(),
      servingStatus: z.string(),
      flightStartDate: z.string().nullable(),
      flightEndDate: z.string().nullable(),
      spendToDate: z.number(),
      pacingPct: z.number().nullable(),
      projectedSpend: z.number().nullable(),
      budgetIds: z.array(z.string()),
      currency: z.string().nullable(),
      role: wireUnion<ProjectCampaign['role']>(),
      counterpartCampaignId: z.string().nullable(),
    }),
  ),
});

export const discoveredCampaignsSchema: z.ZodType<DiscoveredCampaign[]> = z.array(
  z.looseObject({
    id: z.string(),
    connectorId: z.string(),
    platform: z.string(),
    externalCampaignId: z.string(),
    externalCampaignName: z.string(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
  }),
);

export const connectorCampaignsSchema: z.ZodType<{ items: PlatformCampaignOption[] }> =
  z.looseObject({
    items: z.array(
      z.looseObject({
        externalId: z.string(),
        name: z.string(),
        status: z.string(),
        currency: z.string().optional(),
        startDate: z.string().optional(),
        endDate: z.string().optional(),
      }),
    ),
  });

export const linkedCampaignSchema: z.ZodType<{ id: string }> = z.looseObject({ id: z.string() });

const dailyMetricSchema = z.looseObject({
  date: z.string(),
  spend: z.number(),
  impressions: z.number(),
  clicks: z.number(),
  conversions: z.number(),
});

/**
 * `dimension` is the one closed enum here rather than a {@link wireUnion}: the client chose
 * it in the request, so a value outside the four is the server answering a question nobody
 * asked, and every consumer branches on it.
 */
export const projectPerformanceSchema: z.ZodType<ProjectPerformance> = z.looseObject({
  metrics: z.array(dailyMetricSchema),
  spendByCurrency: z.array(
    z.looseObject({ currency: z.string(), spend: z.number(), campaignCount: z.number() }),
  ),
  unknownCurrencySpend: z.number(),
  unknownCurrencyCampaignCount: z.number(),
  isSingleCurrency: z.boolean(),
  impressions: z.number(),
  clicks: z.number(),
  conversions: z.number(),
  dimension: z.enum(['platform', 'campaign', 'creative', 'placement']),
  breakdown: z.array(
    z.looseObject({
      key: z.string(),
      label: z.string(),
      currency: z.string().nullable(),
      spend: z.number(),
      impressions: z.number(),
      clicks: z.number(),
      conversions: z.number(),
      spendReported: z.boolean(),
      previewUrl: z.string().nullable().optional(),
    }),
  ),
  campaignCount: z.number(),
  supersededCampaignIds: z.array(z.string()),
  campaignsWithoutBreakdown: z.array(z.string()),
});

/**
 * An upload target minted against object storage.
 *
 * `uploadFields` stays `unknown`-valued because it is storage's own signature bag, opaque to
 * this app: it is copied into a form body verbatim and nothing here reads a field by name.
 */
export const presignedAssetsSchema = z.array(
  z.looseObject({
    fileName: z.string(),
    assetId: z.string(),
    uploadUrl: z.string(),
    uploadFields: z.record(z.string(), z.unknown()),
  }),
);

export type PresignedAsset = z.infer<typeof presignedAssetsSchema>[number];

export const confirmedAssetsSchema = z.array(
  z.looseObject({
    fileName: z.string(),
    status: z.enum(['stored', 'failed']),
    reason: z.string().optional(),
  }),
);

export type ConfirmedAsset = z.infer<typeof confirmedAssetsSchema>[number];

export const jobAssetUrlSchema = z.looseObject({ url: z.string() });

export const platformFieldSchemasSchema: z.ZodType<ListPlatformFieldSchemasResponse> =
  z.looseObject({
    items: z.array(
      z.looseObject({
        platform: z.string(),
        label: z.string(),
        levels: z.array(
          z.looseObject({ level: z.string(), singular: z.string(), plural: z.string() }),
        ),
        fields: z.array(
          z.looseObject({
            key: z.string(),
            label: z.string(),
            level: z.string(),
            kind: wireUnion<
              ListPlatformFieldSchemasResponse['items'][number]['fields'][number]['kind']
            >(),
            required: z.boolean(),
            creationOnly: z.boolean(),
            waivedBy: z.string().nullish(),
            help: z.string().nullable(),
            example: z.string().nullable(),
            options: z.array(z.looseObject({ value: z.string(), label: z.string() })).nullable(),
          }),
        ),
      }),
    ),
  });
